import { Button } from "../../../components/button";
import { useAuth } from "../../../context/AuthContext";
import TMAddressesInfoDisplay from "./tm-address-info-display";
import { UserStatus } from "../../../types/user.types";
import { Gender, GenderTranslations } from "../../../types/personal_info.types";
import { parseBirthdateToLocalFormat } from "../../../utils/date.utils";
import { TeamWithDetails } from "../../../types/team.types";
import {
  Calendar,
  Droplet,
  FileDigit,
  IdCard,
  Info,
  Mail,
  Mars,
  Phone,
  PhoneCall,
  ShipWheel,
  User,
  Venus,
} from "lucide-react";

export interface ITeamMembersInfoDisplayProps {
  onCreate?: () => void;
  onEdit?: (memberId: number) => void;
  onDelete?: (memberId: number, memberName: string) => void;
  team: TeamWithDetails;
}

export function TeamMembersInfoDisplay({
  onCreate,
  onEdit,
  onDelete,
  team,
}: ITeamMembersInfoDisplayProps) {
  const { user } = useAuth();
  const loggedInUserId = user?.id;

  return (
    <>
      {team?.members && team.members.length > 0 ? (
        <div className="grid grid-cols-1 gap-6">
          <div className="bg-blue-50 p-4 rounded-md border border-blue-200 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-blue-800">
              Para designar a un miembro como timonel del equipo, hacé clic en
              el botón "Editar", activá la opción de timonel y completá su
              número de licencia.
            </p>
          </div>
          {team.members.map((member) => (
            <div
              key={member.id}
              className="bg-white/70 shadow-md rounded-xl p-6 border border-gray-300"
            >
              <div className="p-4 rounded-t-xl border-b border-gray-300">
                <p className="text-lg font-semibold">
                  {member.personal_info?.first_name}{" "}
                  {member.personal_info?.last_name}
                </p>
              </div>

              <div className="p-4 space-y-4">
                {member.user_status === UserStatus.REGISTERED ? (
                  <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
                    <p className="text-yellow-800 font-semibold">
                      ⚠️ Este miembro todavía no ha activado su cuenta.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <p className="text-gray-800 flex flex-wrap items-center gap-1">
                      <User className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">Nombre:</span>{" "}
                      {member.personal_info?.first_name}{" "}
                      {member.personal_info?.last_name}
                    </p>
                    <p className="text-gray-800 flex flex-wrap items-center gap-1">
                      <ShipWheel className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">Timonel:</span>{" "}
                      {member.is_helmsman ? "Sí" : "No"}
                    </p>
                    <p className="text-gray-800 flex flex-wrap items-center gap-1">
                      <Phone className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">Teléfono:</span>{" "}
                      {member.personal_info?.primary_phone_number || "-"}
                    </p>

                    <p className="text-gray-800 flex flex-wrap items-center gap-1">
                      <FileDigit className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">
                        Número de Licencia:
                      </span>{" "}
                      {member.personal_info?.licence_number || "-"}
                    </p>
                    <p className="text-gray-800 flex flex-wrap items-center gap-1">
                      <IdCard className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">DNI:</span>{" "}
                      {member.personal_info?.dni || "-"}
                    </p>
                    <p className="text-gray-800 flex flex-wrap items-center gap-1">
                      <Mail className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">
                        Correo Electrónico:
                      </span>{" "}
                      {member.personal_info?.email || "-"}
                    </p>
                    {member.personal_info?.birth_date && (
                      <p className="text-gray-800 flex flex-wrap items-center gap-1">
                        <Calendar className="w-5 h-5 text-gray-600" />
                        <span className="font-semibold">
                          Fecha de Nacimiento:
                        </span>{" "}
                        {parseBirthdateToLocalFormat(
                          String(member.personal_info?.birth_date)
                        ) || "-"}
                      </p>
                    )}
                    <p className="text-gray-800 flex flex-wrap items-center gap-1">
                      <PhoneCall className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">
                        Contacto de Emergencia:
                      </span>{" "}
                      {member.personal_info?.emergency_contact_number || "-"}
                    </p>
                    <p className="text-gray-800 flex flex-wrap items-center gap-1">
                      {member.personal_info?.gender === Gender.MALE ? (
                        <Mars className="w-5 h-5 text-gray-600" />
                      ) : member.personal_info?.gender === Gender.FEMALE ? (
                        <Venus className="w-5 h-5 text-gray-600" />
                      ) : null}
                      <span className="font-semibold">Género:</span>{" "}
                      {GenderTranslations[member.personal_info?.gender!]}
                    </p>
                    <p className="text-gray-800 flex flex-wrap items-center gap-1">
                      <Droplet className="w-5 h-5 text-gray-600" />
                      <span className="font-semibold">
                        Grupo Sanguíneo:
                      </span>{" "}
                      {member.personal_info?.blood_type || "No especificado"}
                    </p>
                  </div>
                )}
                {member.user_status !== UserStatus.REGISTERED && (
                  <TMAddressesInfoDisplay addresses={member.addresses!} />
                )}
                <div className="flex justify-end space-x-2">
                  {member.user_status !== UserStatus.REGISTERED &&
                    onDelete &&
                    loggedInUserId !== member.user_id && (
                      <Button
                        text="Eliminar"
                        variant="delete"
                        className="mt-4"
                        onClick={() =>
                          onDelete(
                            member.user_id,
                            `${member.personal_info?.first_name} ${member.personal_info?.last_name}`
                          )
                        }
                      />
                    )}
                  {member.user_status !== UserStatus.REGISTERED && onEdit && (
                    <Button
                      text="Editar"
                      variant="edit"
                      className="mt-4"
                      onClick={() => onEdit(member.user_id)}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-600">No hay miembros en el equipo.</p>
      )}
      {team?.members && team.members.length < 3 && onCreate && (
        <Button
          text="Agregar Miembro"
          variant="create"
          className="mt-4"
          onClick={onCreate}
        />
      )}
    </>
  );
}
